import React from "react";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import DashboardService from "../../Services/DashboardService";
import { EmptyIcon } from "../../Common/FullPageSpinner";

interface ISubmissionCardProps {
  practiceId: number | null;
  fromDate: string;
  toDate: string;
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div style={{
        background: "#fff",
        border: "1px solid rgba(63,91,211,0.18)",
        borderRadius: 10,
        padding: "10px 14px",
        boxShadow: "0 4px 20px rgba(15,27,60,0.12)",
        fontFamily: "Inter, sans-serif",
      }}>
        <div style={{ fontSize: 11, color: "#8fa3b8", fontWeight: 500, marginBottom: 5 }}>{label}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: "#3F5BD3" }} />
          <span style={{ fontSize: 15, fontWeight: 800, color: "#3F5BD3" }}>{payload[0].value}</span>
          <span style={{ fontSize: 11, color: "#8fa3b8" }}>submissions</span>
        </div>
      </div>
    );
  }
  return null;
};

const SubmissionCard = (props: ISubmissionCardProps) => {
  const [chartOptions, setChartOptions] = React.useState<any>({
    xData: [],
    yData: [],
  });
  const [loader, setLoader] = React.useState<boolean>(true);

  React.useEffect(() => {
    if (!props?.practiceId) {
      setChartOptions({ xData: [], yData: [] });
      return;
    }
    setLoader(true);
    DashboardService.getSubmissionDetails(
      props?.practiceId,
      props?.fromDate,
      props?.toDate,
    )
      .then((response) => {
        if (response.status && response.result) {
          const xData = Object.keys(response.result);
          const yData = Object.values(response.result);
          for (var i = 0; i < xData.length; i++) {
            xData[i] = xData[i].toUpperCase();
          }
          setChartOptions({ xData, yData });
        } else {
          setChartOptions({ xData: [], yData: [] });
        }
      })
      .catch((error) => {
        console.error("Error fetching chart data:", error);
        setChartOptions({ xData: [], yData: [] });
      })
      .finally(() => {
        setLoader(false);
      });
  }, [props.practiceId, props.fromDate, props.toDate]);

  // Convert xData/yData into the [{name, value}] shape Recharts needs
  const chartData = chartOptions.xData.map((key: string, i: number) => ({
    name: key,
    value: chartOptions.yData[i],
  }));

  return (
    <>
      <Spin
        spinning={loader}
        indicator={<LoadingOutlined style={{ fontSize: 30, color: "#3F5BD3" }} spin />}
      >
        {chartOptions.xData.length > 0 && chartOptions.yData.length > 0 ? (
          <ResponsiveContainer width="100%" height={310}>
            <AreaChart data={chartData} margin={{  right: 32, left: 0,top:8 ,bottom:8 }}>
              <defs>
                <linearGradient id="subFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6c8ef5" stopOpacity={0.18} />
                  <stop offset="100%" stopColor="#3F5BD3" stopOpacity={0.01} />
                </linearGradient>
                <linearGradient id="subStroke" x1="1" y1="0" x2="0" y2="0">
                  <stop offset="0%" stopColor="#6c8ef5" />
                  <stop offset="100%" stopColor="#3F5BD3" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="4 4" stroke="#f0f4ff" vertical={false} />
              <XAxis
                dataKey="name"
                tick={{ fill: "#8fa3b8", fontSize: 11, fontFamily: "Inter, sans-serif" }}
                tickLine={false}
                axisLine={{ stroke: "#e8eef8" }}
                angle={-30}
                textAnchor="end"
                interval={0}
                height={30}
              />
              <YAxis
                tick={{ fill: "#8fa3b8", fontSize: 11, fontFamily: "Inter, sans-serif" }}
                tickLine={false}
                axisLine={false}
                padding={{   
                    bottom: 1.5
                }}
                tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ stroke: "rgba(63,91,211,0.08)", strokeWidth: 2 }} />
              <Area
                type="monotone"
                dataKey="value"
                name="Submissions"
                stroke="url(#subStroke)"
                strokeWidth={3}
                fill="url(#subFill)"
                dot={false}
                activeDot={{ r: 5, fill: "#3F5BD3", stroke: "#fff", strokeWidth: 2 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              marginTop: 80,
              gap: 8,
            }}
          >
            <EmptyIcon />
          </div>
        )}
      </Spin>
    </>
  );
};

export default SubmissionCard;